<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia';
import { useProductStore } from '@/stores/product';
import type { IItem } from '@posx/core';

const $props = defineProps<{
  modelValue?: string[];
  rootFormData?: any;
  depends?: string;
}>();

const $emit = defineEmits(['change', 'update:value', 'update:modelValue']);
const productStore = useProductStore();
const { sortedList: products } = storeToRefs(productStore)
const { t, locale } = useI18n()

// Local selection for current category dropdown display
const localSelection = ref<string[]>([]);

const categoryUid = computed(() => $props.rootFormData?.category_uid || '');

const filteredProducts = computed(() => {
  if (!categoryUid.value) return [];
  return products.value.filter((product: IItem) => product.category_uid === categoryUid.value);
});

// Get product UIDs for current category from full selection
const currentCategoryProductUids = computed(() => {
  const categoryProductUids = new Set(filteredProducts.value.map(p => p.uid));
  return ($props.modelValue || []).filter(uid => categoryProductUids.has(uid));
});

// Sync local selection when category changes or modelValue updates
watch([categoryUid, () => $props.modelValue], () => {
  localSelection.value = currentCategoryProductUids.value;
}, { immediate: true });

// Accumulate selections across categories
const handleChange = () => {
  const categoryProductUids = new Set(filteredProducts.value.map(p => p.uid));
  // Keep selections from other categories + add current category selections
  const otherCategorySelections = ($props.modelValue || []).filter(uid => !categoryProductUids.has(uid));
  const newValue = [...otherCategorySelections, ...localSelection.value];
  $emit('change', newValue);
  $emit('update:value', newValue);
  $emit('update:modelValue', newValue);
};
</script>

<template>
  <el-select
    v-model="localSelection"
    @change="handleChange"
    multiple
    :disabled="!categoryUid"
    :placeholder="t('common.select_product')"
    style="width: 100%"
    collapse-tags
    collapse-tags-tooltip
    teleported
    popper-class="fixed-dropdown"
  >
    <el-option
      v-for="product in filteredProducts"
      :key="product.uid"
      :label="product.name_translations?.[locale] || product.name"
      :value="product.uid"
    />
  </el-select>
</template>

<style scoped>
.fixed-dropdown {
  z-index: 3000 !important;
}
</style>
